import Bell from "~icons/ph/bell";
import { useEffect, useState } from "react";
import House from "~icons/ph/house";
import MessageCircle from "~icons/ph/chat-circle";
import Users from "~icons/ph/users";
import type { Person } from "../../shared/types";
import type { Screen } from "./location";
import { Avatar, Wave } from "../ui/components";
import { number, t } from "../i18n";

export function AppNavigation({
  person,
  requests,
  unread,
  notifications,
  active,
  inert,
  onNavigate,
}: {
  person: Person;
  requests: number;
  unread: number;
  notifications: number;
  active: Screen;
  inert: boolean;
  onNavigate: (view: Screen) => void;
}) {
  const [tooltipView, setTooltipView] = useState<Screen | null>(null);
  useEffect(() => {
    if (!tooltipView) return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key === "Escape") setTooltipView(null);
    };
    window.addEventListener("keydown", dismiss);
    return () => window.removeEventListener("keydown", dismiss);
  }, [tooltipView]);
  const items: {
    view: Screen;
    label: string;
    name?: string;
    description?: string;
    badge?: number;
    icon?: typeof House;
  }[] = [
    {
      view: "home",
      label: t("الرئيسية"),
      name: t("رنّة — الرئيسية"),
      icon: House,
    },
    {
      view: "messages",
      label: t("الرسائل"),
      description: unread
        ? t("{count} غير مقروءة", { count: number(unread) })
        : undefined,
      badge: unread,
      icon: MessageCircle,
    },
    {
      view: "friends",
      label: t("الأصدقاء"),
      description: requests
        ? t("طلبات الإضافة، {count} طلبات", { count: number(requests) })
        : undefined,
      badge: requests,
      icon: Users,
    },
    {
      view: "notifications",
      label: t("الإشعارات"),
      description: notifications
        ? t("إشعارات جديدة، {count}", { count: number(notifications) })
        : undefined,
      badge: notifications,
      icon: Bell,
    },
    { view: "profile", label: t("الحساب"), name: t("حسابك") },
  ];

  return (
    <nav className="app-navigation" aria-label={t("التنقل")} inert={inert}>
      <span className="app-nav-mark" aria-hidden="true">
        <Wave />
      </span>
      <div className="app-nav-items">
        {items.map(({ view, label, name, description, badge, icon: Icon }) => (
          <button
            key={view}
            className={`app-nav-button ${view === active ? "selected" : ""}`}
            data-view={view}
            data-tooltip-active={tooltipView === view || undefined}
            aria-label={name ?? label}
            aria-description={description}
            aria-pressed={view === active}
            aria-current={view === active ? "page" : undefined}
            onClick={() => onNavigate(view)}
            // A tap is neither hovering nor moving focus by keyboard.
            onPointerEnter={(event) => {
              if (event.pointerType === "mouse") setTooltipView(view);
            }}
            onPointerLeave={() =>
              setTooltipView((current) => (current === view ? null : current))
            }
            onFocus={(event) => {
              if (event.currentTarget.matches(":focus-visible"))
                setTooltipView(view);
            }}
            onBlur={() =>
              setTooltipView((current) => (current === view ? null : current))
            }
          >
            {Icon ? (
              <Icon width={24} height={24} aria-hidden="true" />
            ) : (
              <Avatar person={person} />
            )}
            <span className="app-nav-tooltip" role="tooltip">
              {label}
            </span>
            {!!badge && (
              <span className="app-nav-badge" aria-hidden="true">
                {number(badge)}
              </span>
            )}
          </button>
        ))}
      </div>
    </nav>
  );
}
